import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconButton } from 'rsuite';
import TrashIcon from '@rsuite/icons/Trash';
import EditIcon from '@rsuite/icons/Edit';

import BasicPanel from '../../components/panels/basicPanel/basicPanel';
import { getTemplatesByIdentity, deleteTemplate } from '../../api/template';
import BasicWrapper from '../../components/basicWrapper/basicWrapper';
import { Loader, useToaster, Message } from 'rsuite';

import Btn from '../../components/Btn/Btn';
import { useMessenger } from '../../context/messengerContext';
import ConfirmModal from '../../components/confirmModal/confirmModal';

import './templatesList.css';
import { isIOSVersionHigherThan26Version } from '../../utils/getUserAgentParser';

export default function TemplatesList() {
    const { t } = useTranslation('basic');
    const { userId, platform } = useMessenger();
    const navigate = useNavigate();
    const toaster = useToaster();

    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const loadTemplates = async () => {
        setIsLoading(true);
        setIsError(null);
        try {
            const res = await getTemplatesByIdentity({ platform, userId });
            setTemplates(res);
        } catch (err) {
            setIsError(err?.message || t('templates_error'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadTemplates();
    }, [userId]);

    const openDeleteModal = (templateId) => {
        setTemplateToDelete(templateId);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setDeleteModalOpen(false);
        setTemplateToDelete(null);
        setDeleteError(null);
    };

    const handleConfirmDelete = async () => {
        if (templateToDelete == null) return;
        setIsDeleting(true);
        setDeleteError(null);
        try {
            await deleteTemplate(templateToDelete);
            closeDeleteModal();
            await loadTemplates();
            toaster.push(
                <Message type='success' showIcon>
                    {t('templates_delete_success')}
                </Message>,
                { placement: 'topCenter', duration: 3000 },
            );
        } catch (err) {
            const msg = err?.message || t('templates_delete_error');
            setDeleteError(msg);
            toaster.push(
                <Message type='error' showIcon>
                    {msg}
                </Message>,
                { placement: 'topCenter', duration: 3000 },
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const isIOS26Version = isIOSVersionHigherThan26Version();

    if (isLoading) {
        return (
            <div className='loader-container'>
                <Loader size='lg' />
            </div>
        );
    }

    if (isError) {
        return (
            <div
                className={`templatesList ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}
            >
                <BasicPanel>
                    <p className='templatesList_error'>{isError}</p>
                </BasicPanel>
            </div>
        );
    }

    return (
        <div
            className={`stepsHeaderWrapper gradient_bg_header ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}
        >
            <BasicWrapper>
                <div className='templatesList_header'>
                    <div className='stepHeaderHeader heading-h4'>
                        {t('templates_list_title')}
                        <img
                            id='stepsHeaderImage'
                            src='https://s3.timeweb.com/8a6e091b-publick/template_header.png'
                            alt=''
                        />
                    </div>
                    <Btn
                        text={t('templates_create')}
                        fluid
                        onClick={() => navigate('/template_create')}
                    />
                </div>

                {templates.length === 0 && <p>{t('templates_empty')}</p>}
                {templates.length > 0 && (
                    <ul className='templatesList_list'>
                        {templates.map((item) => {
                            return (
                                <li
                                    key={item.id}
                                    className='templatesList_card'
                                >
                                    <div className='templatesList_cardRow'>
                                        {t('templates_template_name')}:{' '}
                                        <span className='templatesList_cardRowValue'>
                                            {item?.template_name ??
                                                `${t('templates_template_name')}${item.template_id}`}
                                        </span>
                                    </div>
                                    <div className='templatesList_cardRow'>
                                        {t('templates_contract_number')}:{' '}
                                        <span className='templatesList_cardRowValue'>
                                            {item?.contract_number ?? '—'}
                                        </span>
                                    </div>
                                    <div className='templatesList_cardRow'>
                                        {t('templates_role')}:{' '}
                                        <span className='templatesList_cardRowValue'>
                                            {item?.who_pay
                                                ? t(`who_pay_${item.who_pay}`)
                                                : '—'}
                                        </span>
                                    </div>
                                    <div className='templatesList_cardRow'>
                                        {t('templates_from_address')}:{' '}
                                        <span className='templatesList_cardRowValue'>
                                            {item?.city_from_label || '—'}
                                        </span>
                                    </div>
                                    <div className='templatesList_cardRow'>
                                        {t('templates_to_address')}:{' '}
                                        <span className='templatesList_cardRowValue'>
                                            {item?.city_to_label || '—'}
                                        </span>
                                    </div>
                                    <div className='templatesList_cardActions'>
                                        <div
                                            className='templatesList_cardEdit'
                                            role='button'
                                            tabIndex={0}
                                            onClick={() =>
                                                navigate(
                                                    `/template_update/${item.id}?contract_number=${encodeURIComponent(
                                                        item?.contract_number ??
                                                            '',
                                                    )}`,
                                                )
                                            }
                                            onKeyDown={(event) => {
                                                if (
                                                    event.key === 'Enter' ||
                                                    event.key === ' '
                                                ) {
                                                    event.preventDefault();
                                                    navigate(
                                                        `/template_update/${item.id}?contract_number=${encodeURIComponent(
                                                            item?.contract_number ??
                                                                '',
                                                        )}`,
                                                    );
                                                }
                                            }}
                                            aria-label={t('templates_edit')}
                                        >
                                            <IconButton
                                                icon={<EditIcon />}
                                                appearance='subtle'
                                                size='md'
                                            />
                                        </div>
                                        <div
                                            className='templatesList_cardDelete'
                                            role='button'
                                            tabIndex={0}
                                            onClick={() =>
                                                openDeleteModal(item.id)
                                            }
                                            onKeyDown={(event) => {
                                                if (
                                                    event.key === 'Enter' ||
                                                    event.key === ' '
                                                ) {
                                                    event.preventDefault();
                                                    openDeleteModal(item.id);
                                                }
                                            }}
                                            aria-label={t('templates_delete')}
                                        >
                                            <IconButton
                                                icon={<TrashIcon />}
                                                appearance='subtle'
                                                color='red'
                                                size='md'
                                            />
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}

                <ConfirmModal
                    open={deleteModalOpen}
                    onClose={closeDeleteModal}
                    onConfirm={handleConfirmDelete}
                    titleKey='templates_delete_confirm_title'
                    bodyKey='templates_delete_warning'
                    confirmTextKey='templates_delete_confirm_btn'
                    error={deleteError}
                    isLoading={isDeleting}
                />
            </BasicWrapper>
        </div>
    );
}
